/**
 * VistoeraTASK-00597 — pure crop-shape / aspect-lock helpers.
 * No Canvas dependency so node-tests can cover the contract.
 */

/** VistoeraTASK-00747 — map image-space AABB through viewport scale+offset. */
export function imageRectToScreen(
  r: { x: number; y: number; w: number; h: number },
  scale: number,
  offset: { x: number; y: number },
): { x: number; y: number; w: number; h: number } {
  const s = Math.max(scale, 0.0001)
  const x = r.x * s + offset.x
  const y = r.y * s + offset.y
  return { x, y, w: r.w * s, h: r.h * s }
}

/** VistoeraTASK-00747 — Fit-to-View scale (never forces 1.0 when host is ready). */
export function computeFitScale(
  imageW: number,
  imageH: number,
  hostW: number,
  hostH: number,
  pad = 20,
  minScale = 0.02,
): number | null {
  if (!(imageW > 0 && imageH > 0 && hostW >= 80 && hostH >= 120)) return null
  const sx = (hostW - pad) / imageW
  const sy = (hostH - pad) / imageH
  return Math.max(minScale, Math.min(sx, sy, 1))
}

export type CropShape = 'rect' | 'square' | 'ellipse' | 'circle'

export interface CropRect {
  x: number
  y: number
  w: number
  h: number
  shape: CropShape
  invert: boolean
}

export function isEllipseLike(shape: CropShape): boolean {
  return shape === 'ellipse' || shape === 'circle'
}

export function isAspectLocked(shape: CropShape, shiftHeld: boolean): boolean {
  if (shape === 'square' || shape === 'circle') return true
  if (shape === 'rect' || shape === 'ellipse') return shiftHeld
  return false
}

/** Build a create-drag rect from start→pointer, optionally 1:1 locked. */
export function rectFromCreateDrag(
  start: { x: number; y: number },
  pointer: { x: number; y: number },
  locked: boolean,
): { x: number; y: number; w: number; h: number } {
  const dx = pointer.x - start.x
  const dy = pointer.y - start.y
  if (!locked) {
    return {
      x: Math.min(start.x, pointer.x),
      y: Math.min(start.y, pointer.y),
      w: Math.abs(dx),
      h: Math.abs(dy),
    }
  }
  const side = Math.max(Math.abs(dx), Math.abs(dy))
  return {
    x: dx >= 0 ? start.x : start.x - side,
    y: dy >= 0 ? start.y : start.y - side,
    w: side,
    h: side,
  }
}

/**
 * After a free resize, force 1:1 for square/circle modes (and Shift on
 * free shapes). Anchors from the opposite corner of the dragged edge
 * when possible; otherwise grows from top-left.
 */
export function enforceAspectRatio(
  r: { x: number; y: number; w: number; h: number },
  locked: boolean,
  mode: string,
): { x: number; y: number; w: number; h: number } {
  if (!locked) return r
  let { x, y, w, h } = r
  const side = Math.max(Math.abs(w), Math.abs(h))
  // Keep the corner opposite the resize handle fixed when possible.
  if (mode.includes('w') && !mode.includes('e')) {
    x = r.x + r.w - side
  }
  if (mode.includes('n') && !mode.includes('s')) {
    y = r.y + r.h - side
  }
  if (mode === 'move' || mode === 'create') {
    /* keep x,y as provided */
  }
  return { x, y, w: side, h: side }
}

/** Snap an existing free rect to 1:1 when the user switches to square/circle. */
export function snapToSquareCentered(r: { x: number; y: number; w: number; h: number }): {
  x: number
  y: number
  w: number
  h: number
} {
  const side = Math.min(r.w, r.h)
  const cx = r.x + r.w / 2
  const cy = r.y + r.h / 2
  return { x: cx - side / 2, y: cy - side / 2, w: side, h: side }
}

export function normalizeCropRect(
  partial: Partial<CropRect> & { x: number; y: number; w: number; h: number },
  defaults?: { shape?: CropShape; invert?: boolean },
): CropRect {
  return {
    x: partial.x,
    y: partial.y,
    w: partial.w,
    h: partial.h,
    shape: partial.shape ?? defaults?.shape ?? 'rect',
    invert: partial.invert ?? defaults?.invert ?? false,
  }
}

/** Output canvas size rules (§七). */
export function cropOutputSize(
  crop: CropRect,
  imageW: number,
  imageH: number,
): { sx: number; sy: number; sw: number; sh: number } {
  if (crop.invert) {
    return { sx: 0, sy: 0, sw: imageW, sh: imageH }
  }
  return {
    sx: Math.round(crop.x),
    sy: Math.round(crop.y),
    sw: Math.max(1, Math.round(crop.w)),
    sh: Math.max(1, Math.round(crop.h)),
  }
}

/**
 * VistoeraTASK-00618 — apply numeric W/H from the props panel.
 *
 * Max size is the full image (not `imageW - x`). When the requested size
 * no longer fits at the current top-left, shift x/y just enough to stay
 * in-bounds (prefer keeping the previous origin when possible).
 * Square/circle stay 1:1. Does not change mouse-drag clamp semantics.
 */
export function applyNumericCropSize(
  cur: CropRect,
  size: { w?: number; h?: number },
  imageW: number,
  imageH: number,
  minSize = 4,
): CropRect {
  const iw = Math.max(1, Math.round(imageW))
  const ih = Math.max(1, Math.round(imageH))
  const min = Math.max(1, minSize)
  const locked = cur.shape === 'square' || cur.shape === 'circle'

  let nextW = cur.w
  let nextH = cur.h
  let nextX = cur.x
  let nextY = cur.y

  if (locked) {
    const sideReq = size.w !== undefined ? size.w : size.h !== undefined ? size.h : Math.min(cur.w, cur.h)
    const sideRaw = Number.isFinite(sideReq) ? (sideReq as number) : Math.min(cur.w, cur.h)
    const side = Math.min(Math.max(min, sideRaw), iw, ih)
    nextW = side
    nextH = side
    nextX = Math.min(Math.max(0, cur.x), Math.max(0, iw - side))
    nextY = Math.min(Math.max(0, cur.y), Math.max(0, ih - side))
  } else {
    if (size.w !== undefined) {
      const raw = Number.isFinite(size.w) ? size.w : cur.w
      nextW = Math.min(Math.max(min, raw), iw)
      nextX = Math.min(Math.max(0, cur.x), Math.max(0, iw - nextW))
    }
    if (size.h !== undefined) {
      const raw = Number.isFinite(size.h) ? size.h : cur.h
      nextH = Math.min(Math.max(min, raw), ih)
      nextY = Math.min(Math.max(0, cur.y), Math.max(0, ih - nextH))
    }
  }

  return normalizeCropRect({
    x: nextX,
    y: nextY,
    w: nextW,
    h: nextH,
    shape: cur.shape,
    invert: cur.invert,
  })
}

/** True when the crop produces transparent pixels that must stay PNG. */
export function cropRequiresTransparency(crop: CropRect | null | undefined): boolean {
  if (!crop) return false
  if (crop.invert) return true
  return isEllipseLike(crop.shape)
}
